import type { PayloadAction } from '@reduxjs/toolkit';
import { getErrorMsg, logger } from 'lib/logger';
import { BillingStartEvent, EventStartAPICall } from 'reactApp/api/billing/EventStartAPICall';
import { PromocodeAPICall } from 'reactApp/api/promo/PromocodeAPICall';
import { IS_SOCIAL_USER, IS_WEBVIEW, IS_WEBVIEW_FROM_UA } from 'reactApp/appHelpers/configHelpers';
import { billingPersonalizationEnabled } from 'reactApp/appHelpers/featuresHelpers/features/billingPersonalization';
import { quotaLandingLoadProductsTimeout } from 'reactApp/appHelpers/featuresHelpers/features/quotaLandingLoadProductsTimeouts';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { loadBillingDataIfNotLoaded } from 'reactApp/modules/billingData/billingData.provider';
import { billingPersonalizationLoadRequest } from 'reactApp/modules/billingPersonalization/billingPersonalization.module';
import { BillingPersonalizationSelectors } from 'reactApp/modules/billingPersonalization/billingPersonalization.selectors';
import { CardActions } from 'reactApp/modules/creditCard/creditCard.module';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { productsController } from 'reactApp/modules/products/products.controller';
import { initProducts, initProductsForAnonymous } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { initPromoTariffs as initPromoTariffsAction } from 'reactApp/modules/promoTariffs/promoTariffs.module';
import {
    activatePromocodeRequest,
    loadQuotaLandingFailure,
    loadQuotaLandingProducts,
    loadQuotaLandingRequest,
    loadQuotaLandingSuccess,
    loadQuotaTariffsLandingRequest,
    sendShowLandingAnalyticsAction,
    showLandingTariffsAction,
} from 'reactApp/modules/quotaLanding/quotaLanding.module';
import type { ActivatePromocode } from 'reactApp/modules/quotaLanding/quotaLanding.types';
import { ReferralProgramActions } from 'reactApp/modules/referralProgram/referralProgram.module';
import { routeChangeSuccess } from 'reactApp/modules/router/router.module';
import { getQuotaLandingStorage } from 'reactApp/modules/router/router.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { loadSocialUser } from 'reactApp/modules/socialUser/actions/loadSocialUser.action';
import { getIdByStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { loadUserQuotaSuccess } from 'reactApp/modules/userQuota/userQuota.module';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { closePromocodeModal } from 'reactApp/sections/QuotaLanding/PromocodeDialog/PromocodeDialog.helpers';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { store as reduxStore } from 'reactApp/store';
import { EPromocodeApiErrorCodes, isActivateError } from 'reactApp/ui/Promocode/promocode.constants';
import { sendXray } from 'reactApp/utils/ga';
import { isIntersectionObserverSupported } from 'reactApp/utils/intersectionObserverSupported';
import opener from 'reactApp/utils/opener';
import { getPeriodName } from 'reactApp/utils/Period';
import { call, cancel, put, putResolve, take, takeEvery } from 'redux-saga/effects';
import { select } from 'typed-redux-saga';

const activatePromocodeAPICall = (promocode: string) => new PromocodeAPICall().makeRequest({ promocode });

function* sendShowLandingAnalytics() {
    const { isLoaded } = yield* select(UserQuotaSelectors.getLoadingState);

    if (!isLoaded) {
        yield take(loadUserQuotaSuccess);
    }

    yield call(sendQuotaGa, { action: 'landing', label: 'view' });
}

function* startLandingQuotaTariffs() {
    const storage = getQuotaLandingStorage();

    yield put(
        routeChangeSuccess({
            id: ROOT_FOLDER_ID,
            storage,
            params: {},
            __isFolder: true,
            url: '',
            __parent: '',
        })
    );
    try {
        yield put(initProducts());
        if (IS_SOCIAL_USER) {
            yield putResolve(yield call(loadSocialUser));
        }

        // tempexp_19915-start
        // @TODO: надо ли ниже опускать?
        if (billingPersonalizationEnabled) {
            const { isLoaded } = yield* select(BillingPersonalizationSelectors.getLoadingState);
            if (!isLoaded) {
                yield put(billingPersonalizationLoadRequest());
            }
        }
        // tempexp_19915-end

        yield put(loadQuotaLandingSuccess());

        yield put(initPromoTariffsAction({}));
        yield put(CardActions.loadCard());
        yield call(loadBillingDataIfNotLoaded);
    } catch (error) {
        logger.error(getErrorMsg(error), error);
        yield put(loadQuotaLandingFailure());
        yield cancel();
    }
}

function* startLandingQuota() {
    const storage = getQuotaLandingStorage();

    yield put(
        routeChangeSuccess({
            id: ROOT_FOLDER_ID,
            storage,
            params: {},
            __isFolder: true,
            url: '',
            __parent: '',
        })
    );
    const queryParams = getQueryParams();
    // tempexp_18086-start
    yield put(ReferralProgramActions.startPromoQuotaReferral());
    // tempexp_18086-end

    const isAnonymous = yield* select(UserSelectors.isAnonymous);

    if (isAnonymous) {
        yield put(initProductsForAnonymous());
        yield put(loadQuotaLandingSuccess());
        return;
    }

    /**
     * Грузим продукты только если заходим на лендинг произошел по action-ссылке.
     * В противном случае будем загружать когда юзер доскролит до блока с тарифами.
     */
    if (
        !isIntersectionObserverSupported ||
        !quotaLandingLoadProductsTimeout ||
        ((queryParams?.action === 'request-payment' || queryParams?.action === 'request-family') &&
            (queryParams?.productId || queryParams?.tariffId))
    ) {
        try {
            yield put(initProducts());
        } catch (error) {
            logger.error(getErrorMsg(error), error);
            yield put(loadQuotaLandingFailure());
            yield cancel();
        }
    }

    // tempexp_19915-start
    if (billingPersonalizationEnabled) {
        const { isLoaded } = yield* select(BillingPersonalizationSelectors.getLoadingState);
        if (!isLoaded) {
            yield put(billingPersonalizationLoadRequest());
        }
    }
    // tempexp_19915-end

    yield put(loadQuotaLandingSuccess());
}

function* showLandingTariffs() {
    try {
        const isAnonymous = yield* select(UserSelectors.isAnonymous);

        if (!isAnonymous) {
            yield call(new EventStartAPICall().makeRequest, { event: BillingStartEvent.mailSpaceTariffs });
        }
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}

function* startLoadQuotaLandingProducts() {
    try {
        yield put(initProducts());
    } catch (error) {
        logger.error(getErrorMsg(error), error);
        yield cancel();
    }
}

function* activatePromocode(action: PayloadAction<ActivatePromocode>) {
    const { promocode, onActivate, onError } = action.payload;
    const isWebview = IS_WEBVIEW || IS_WEBVIEW_FROM_UA;

    try {
        const isMobile = EnvironmentSelectors.isMobile();
        const { data } = yield call(activatePromocodeAPICall, promocode);

        yield productsController.loadProducts();

        const productId =
            (typeof data?.services?.unlocked?.web === 'string' && data?.services?.unlocked?.web) ||
            data?.services?.unlocked?.web?.product_id;
        const product = productId ? yield* select(ProductsSelectors.getProductById, productId) : null;

        if (productId && product) {
            const title = product?.trialPeriod
                ? `Подписка Mail Space на ${getPeriodName(product.trialPeriod)} бесплатно`
                : `Подписка Mail Space на ${getPeriodName(product.period)} со скидкой`;

            yield call(onActivate);

            yield put(
                openTariffBuy({
                    productId,
                    title,
                    isMobile,
                    paySource: 'promocodes_mp',
                    hideOtherTariffs: true,
                    hideSnackbars: true,
                    onSuccess: () => {
                        reduxStore.dispatch(
                            showSnackbarAction({
                                id: 'promocode-success',
                                text: 'Промокод активирован. Все активные подписки есть в разделе «Мои подписки»',
                                type: SnackbarTypes.success,
                                closable: true,
                                buttonText: 'Перейти',
                                onButtonClick: () => {
                                    opener(getIdByStorage(EStorageType.subscriptions), !!isWebview);
                                },
                            })
                        );
                        closePromocodeModal();
                    },
                })
            );
            yield cancel();
        } else if (productId && !product) {
            sendXray(['promocode', 'landing', 'fail']);

            yield call(onError, EPromocodeApiErrorCodes.not_new_user);
            yield cancel();
        }

        // Активация подарка.
        yield put(
            showSnackbarAction({
                id: 'promocode-success',
                text: 'Промокод активирован. Все активные подписки есть в разделе «Мои подписки»',
                type: SnackbarTypes.success,
                closable: true,
                buttonText: 'Перейти',
                onButtonClick: () => {
                    opener(getIdByStorage(EStorageType.subscriptions), !!isWebview);
                },
            })
        );
        yield call(closePromocodeModal);
    } catch (errorData) {
        logger.error(errorData);
        sendXray(['promocode', 'landing', 'fail']);

        const errorCode = isActivateError(errorData) ? errorData.error : EPromocodeApiErrorCodes.internal;
        yield call(onError, errorCode);
    }
}

export function* watchQuotaLanding() {
    yield takeEvery(loadQuotaLandingRequest.toString(), startLandingQuota);
    yield takeEvery(loadQuotaTariffsLandingRequest.toString(), startLandingQuotaTariffs);
    yield takeEvery(showLandingTariffsAction.toString(), showLandingTariffs);
    yield takeEvery(sendShowLandingAnalyticsAction.toString(), sendShowLandingAnalytics);
    yield takeEvery(loadQuotaLandingProducts.toString(), startLoadQuotaLandingProducts);
    yield takeEvery(activatePromocodeRequest.toString(), activatePromocode);
}
